/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import React, { useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import {
  CheckmarkStroke,
  EmailSimple as Email,
  Facebook,
  Line,
  Link,
  LinkedIn,
  Twitter,
  X,
} from '@bloomberg/media-ui-icons';

import { useSlug } from '../hooks/useSlug';
import { IconButton } from '../index';
import Portal from '../Portal';
import type { GlobalProps } from '../types';
import { classes } from '../utils';
import {
  TranslatedMessage,
  useTranslateMessage,
} from '../utils/translate-messages';

import styles from './SocialShare.module.css';

export type SocialClickHandler = () => void | ((event: Event) => void);
export type SocialClickHandlers = {
  facebook?: SocialClickHandler;
  /** @deprecated use x network instead */
  twitter?: SocialClickHandler;
  email?: SocialClickHandler;
  linkedin?: SocialClickHandler;
  link?: SocialClickHandler;
  x?: SocialClickHandler;
  line?: SocialClickHandler;
};

export type SocialShareProps = GlobalProps & {
  socials?: SocialClickHandlers;
  vertical?: boolean;
  label?: string;
  showLabel?: boolean;
  copiedToastClassName?: string;
};

const BUTTON_UNCLICK_TIMER = 2000;
const MAX_SOCIAL_COUNT_BEFORE_WRAP = 5;

export const SocialShare = React.forwardRef<HTMLDivElement, SocialShareProps>(
  function SocialShareComponent(props: SocialShareProps, ref) {
    const translateMessage = useTranslateMessage();
    const {
      name = 'social-share',
      socials,
      unstyled,
      vertical = false,
      label = translateMessage({
        id: 'media-ui-core_shareThisArticle',
        defaultMessage: 'Share this article',
      }),
      showLabel = true,
      copiedToastClassName,
    } = props;

    const [copied, setCopied] = useState(false);
    const [componentName] = useSlug(name);
    const copyRef = useRef<HTMLButtonElement>(null);
    const toastRef = useRef<HTMLDivElement>(null);

    const handleCopy = () => {
      if (socials?.link) {
        socials.link();
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, BUTTON_UNCLICK_TIMER);
      }
    };

    const cx = classes([], {
      ...props,
      unstyled,
      name,
    });

    const numOfSocials = socials
      ? Object.values(socials).filter(
          (socialCallback) => typeof socialCallback === 'function'
        ).length
      : 0;

    const cxInnerDiv = classes([
      styles.wrapper,
      vertical && styles.verticalAligned,
      numOfSocials > MAX_SOCIAL_COUNT_BEFORE_WRAP && styles.flexWrapped,
    ]);

    const boundingBox = useMemo(
      () => copyRef.current?.getBoundingClientRect(),
      [copied]
    );

    /** TODO : eslint complains(rightly) thats the optional check below can result in undefined
     * and should be fixed */
    const styleObject = {
      '--toaster-top': `${
        (typeof window !== 'undefined' ? window.scrollY : 0) +
        boundingBox?.top! -
        boundingBox?.height! / 2 +
        10
      }px`,
      '--toaster-left': `${boundingBox?.left! + boundingBox?.width!}px`,
    } as React.CSSProperties;

    return (
      <div className={cx} ref={ref} data-component={componentName}>
        {showLabel && !vertical && (
          <h2 className={styles.label} data-component="label">
            {label}
          </h2>
        )}
        <div className={cxInnerDiv} data-component="social-button-list">
          <div className={styles.socialRow}>
            {socials?.line && (
              <IconButton
                variant="outlined"
                onClick={socials?.line}
                title={`${label} on Line (opens in a new window)`}
              >
                <Line />
              </IconButton>
            )}
            <IconButton
              variant="outlined"
              onClick={socials?.facebook}
              title={`${label} on Facebook (opens in a new window)`}
            >
              <Facebook />
            </IconButton>

            {socials?.twitter && !socials?.x && (
              <IconButton
                variant="outlined"
                onClick={socials?.twitter}
                title={`${label} on Twitter (opens in a new window)`}
              >
                <Twitter />
              </IconButton>
            )}

            {socials?.x && (
              <IconButton
                variant="outlined"
                onClick={socials?.x}
                title={`${label} on X (opens in a new window)`}
              >
                <X />
              </IconButton>
            )}
            <IconButton
              variant="outlined"
              onClick={socials?.linkedin}
              title={`${label} on LinkedIn (opens in a new window)`}
            >
              <LinkedIn />
            </IconButton>
          </div>
          <div className={styles.socialRow}>
            <IconButton
              variant="outlined"
              onClick={socials?.email}
              title={`${label} via email (opens native email client)`}
            >
              <Email />
            </IconButton>
            <IconButton variant="outlined" onClick={handleCopy} ref={copyRef}>
              <Link />
            </IconButton>
            <Portal>
              <CSSTransition
                in={copied}
                timeout={BUTTON_UNCLICK_TIMER}
                mountOnEnter
                unmountOnExit
                nodeRef={toastRef}
                classNames={{
                  enter: styles.enter,
                }}
              >
                <div
                  ref={toastRef}
                  className={classes([copiedToastClassName, styles.copiedBox])}
                  style={styleObject}
                >
                  <div className={styles.copiedBoxOutline}>
                    <CheckmarkStroke className={styles.checkMark} />
                    <TranslatedMessage
                      id="media-ui-core_linkCopied"
                      defaultMessage="Link Copied"
                    />
                    <div className={styles.arrow}></div>
                  </div>
                </div>
              </CSSTransition>
            </Portal>
          </div>
        </div>
      </div>
    );
  }
);
